import React, { useState, useEffect } from 'react'
import {
  videreUndersokelserMaler,
  undersokelsesKategorier,
  getMal,
  prioritetEmoji,
  prioritetBeskrivelse,
  getRelevanteCategoriesForBygningsdel
} from '../data/videreUndersokelserMaler'
import './VidereUndersokelser.css'

function VidereUndersokelser({
  eiendomId,
  bygningsdelId,
  bygningsdelType,
  eksisterendeUndersokelser = [],
  regelForslag = [],
  onUndersokelseOpprettet,
  onUndersokelseSlettet
}) {
  // Helper: Formater bygningsdeltype til lesbar tekst
  const formaterBygningsdel = (type) => {
    const mapping = {
      'yttertak': 'yttertaket',
      'yttervegger': 'ytterveggene',
      'vinduer_dorer': 'vinduer og dører',
      'fundament_sokkel': 'fundament og sokkel',
      'drenering': 'dreneringen',
      'elektrisk': 'elektrisk anlegg',
      'vvs': 'VVS-anlegg',
      'ventilasjon': 'ventilasjonsanlegget',
      'brannsikkerhet': 'brannsikkerhet'
    }
    return mapping[type] || type || 'denne bygningsdelen'
  }

  const [visSkjema, setVisSkjema] = useState(false)
  const [valgtKategori, setValgtKategori] = useState('')
  const [formData, setFormData] = useState({
    arsak: '',
    omfang: '',
    metode: '',
    egendefinert_tillegg: '',
    kostnad_min: '',
    kostnad_max: '',
    kostnad_kommentar: '',
    prioritet: 'anbefalt',
    hastegrad: 'normal'
  })
  const [standardtekstBrukt, setStandardtekstBrukt] = useState(true)
  const [lagrer, setLagrer] = useState(false)

  // Auto-fyll standardtekst når kategori velges
  useEffect(() => {
    if (valgtKategori && standardtekstBrukt) {
      const mal = getMal(valgtKategori)
      if (mal) {
        setFormData({
          ...formData,
          arsak: mal.arsak,
          omfang: mal.omfang,
          metode: mal.metode || '',
          kostnad_min: mal.kostnad_min || '',
          kostnad_max: mal.kostnad_max || '',
          prioritet: mal.prioritet
        })
      }
    }
  }, [valgtKategori, standardtekstBrukt])

  const handleKategoriEndring = (e) => {
    setValgtKategori(e.target.value)
    setStandardtekstBrukt(true)
  }

  const handleInputChange = (field, value) => {
    setFormData({ ...formData, [field]: value })
    // Merk at standardtekst ikke lenger brukes hvis bruker endrer
    if (['arsak', 'omfang', 'metode'].includes(field)) {
      setStandardtekstBrukt(false)
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLagrer(true)

    try {
      const mal = getMal(valgtKategori)
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'https://takstmann-api.swagner.workers.dev'}/api/eiendom/${eiendomId}/videre-undersokelser`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({
            bygningsdel_id: bygningsdelId,
            undersokelse_type: mal.type,
            undersokelse_kategori: valgtKategori,
            arsak: formData.arsak,
            omfang: formData.omfang,
            metode: formData.metode || null,
            standardtekst_brukt: standardtekstBrukt,
            egendefinert_tillegg: formData.egendefinert_tillegg || null,
            kostnad_min: formData.kostnad_min ? parseInt(formData.kostnad_min) : null,
            kostnad_max: formData.kostnad_max ? parseInt(formData.kostnad_max) : null,
            kostnad_kommentar: formData.kostnad_kommentar || null,
            prioritet: formData.prioritet,
            hastegrad: formData.hastegrad
          })
        }
      )

      if (response.ok) {
        const data = await response.json()
        alert('Anbefaling opprettet!')
        setVisSkjema(false)
        setValgtKategori('')
        setFormData({
          arsak: '',
          omfang: '',
          metode: '',
          egendefinert_tillegg: '',
          kostnad_min: '',
          kostnad_max: '',
          kostnad_kommentar: '',
          prioritet: 'anbefalt',
          hastegrad: 'normal'
        })
        if (onUndersokelseOpprettet) onUndersokelseOpprettet(data.id)
      } else {
        const error = await response.json()
        alert(`Feil: ${error.error}`)
      }
    } catch (error) {
      console.error('Feil ved lagring:', error)
      alert('Kunne ikke lagre anbefaling')
    } finally {
      setLagrer(false)
    }
  }

  const handleSlett = async (id) => {
    if (!window.confirm('Er du sikker på at du vil slette denne anbefalingen?')) return

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'https://takstmann-api.swagner.workers.dev'}/api/videre-undersokelser/${id}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      )

      if (response.ok) {
        alert('Anbefaling slettet')
        if (onUndersokelseSlettet) onUndersokelseSlettet(id)
      } else {
        alert('Kunne ikke slette anbefaling')
      }
    } catch (error) {
      console.error('Feil ved sletting:', error)
      alert('Kunne ikke slette anbefaling')
    }
  }

  return (
    <div className="videre-undersokelser">
      <h3>
        {bygningsdelType
          ? `💡 Videre undersøkelser for ${formaterBygningsdel(bygningsdelType)}`
          : '💡 Generelle videre undersøkelser for eiendommen'
        }
      </h3>

      {/* Regelforslag fra backend */}
      {regelForslag && regelForslag.length > 0 && (
        <div className="regelforslag">
          <h4>🤖 Automatiske forslag (basert på svar)</h4>
          {regelForslag.map((regel, idx) => (
            <div key={idx} className="regelforslag-item">
              <div className="regelforslag-header">
                <span className="regelforslag-ikon">⚠️</span>
                <strong>{regel.regel_navn}</strong>
              </div>
              <p className="regelforslag-advarsel">{regel.advarsel_tekst}</p>
              <button
                onClick={() => {
                  // Auto-fyll skjema med regelens mal
                  const mal = JSON.parse(regel.undersokelse_mal)
                  setValgtKategori(mal.kategori)
                  setFormData({
                    arsak: mal.arsak,
                    omfang: mal.omfang,
                    metode: mal.metode || '',
                    kostnad_min: mal.kostnad_min || '',
                    kostnad_max: mal.kostnad_max || '',
                    prioritet: mal.prioritet,
                    hastegrad: 'normal'
                  })
                  setStandardtekstBrukt(true)
                  setVisSkjema(true)
                }}
                className="btn-forslag"
              >
                Bruk denne anbefalingen
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Eksisterende undersøkelser */}
      {eksisterendeUndersokelser.length > 0 && (
        <div className="eksisterende-undersokelser">
          <h4>📋 Registrerte anbefalinger ({eksisterendeUndersokelser.length})</h4>
          {eksisterendeUndersokelser.map(u => (
            <div key={u.id} className={`undersokelse-item prioritet-${u.prioritet}`}>
              <div className="undersokelse-header">
                <span className="prioritet-badge">
                  {prioritetEmoji[u.prioritet]} {u.prioritet.toUpperCase()}
                </span>
                <span className="kategori-badge">{getMal(u.undersokelse_kategori)?.navn || u.undersokelse_kategori}</span>
                <button onClick={() => handleSlett(u.id)} className="btn-slett">🗑️</button>
              </div>
              <div className="undersokelse-content">
                <p><strong>Årsak:</strong> {u.arsak}</p>
                <p><strong>Omfang:</strong> {u.omfang}</p>
                {u.metode && <p><strong>Metode:</strong> {u.metode}</p>}
                {u.egendefinert_tillegg && (
                  <p className="egendefinert"><strong>Tillegg:</strong> {u.egendefinert_tillegg}</p>
                )}
                {(u.kostnad_min || u.kostnad_max) && (
                  <p className="kostnad">
                    <strong>Estimert kostnad:</strong>{' '}
                    {u.kostnad_min && u.kostnad_max
                      ? `${u.kostnad_min.toLocaleString()} - ${u.kostnad_max.toLocaleString()} kr`
                      : u.kostnad_min
                        ? `fra ${u.kostnad_min.toLocaleString()} kr`
                        : `opp til ${u.kostnad_max.toLocaleString()} kr`
                    }
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Knapp for å legge til ny undersøkelse */}
      {!visSkjema && (
        <button onClick={() => setVisSkjema(true)} className="btn-legg-til">
          {bygningsdelType
            ? `➕ Legg til videre undersøkelse for ${formaterBygningsdel(bygningsdelType)}`
            : '➕ Legg til generell videre undersøkelse'
          }
        </button>
      )}

      {/* Skjema for ny undersøkelse */}
      {visSkjema && (
        <form onSubmit={handleSubmit} className="undersokelse-skjema">
          <h4>
            {bygningsdelType
              ? `Ny videre undersøkelse for ${formaterBygningsdel(bygningsdelType)}`
              : 'Ny generell videre undersøkelse'
            }
          </h4>

          {/* Dropdown med kategorier */}
          <div className="form-group">
            <label>Type undersøkelse *</label>
            <select
              value={valgtKategori}
              onChange={handleKategoriEndring}
              required
            >
              <option value="">-- Velg type --</option>
              {Object.entries(
                bygningsdelType
                  ? getRelevanteCategoriesForBygningsdel(bygningsdelType)
                  : undersokelsesKategorier
              ).map(([key, kat]) => (
                <optgroup key={key} label={kat.navn}>
                  {kat.typer.map(type => (
                    <option key={type} value={type}>
                      {getMal(type)?.navn}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
          </div>

          {valgtKategori && (
            <>
              {/* Prioritet */}
              <div className="form-group">
                <label>Prioritet *</label>
                <select
                  value={formData.prioritet}
                  onChange={(e) => handleInputChange('prioritet', e.target.value)}
                  required
                >
                  <option value="kritisk">{prioritetEmoji.kritisk} Kritisk</option>
                  <option value="anbefalt">{prioritetEmoji.anbefalt} Anbefalt</option>
                  <option value="valgfri">{prioritetEmoji.valgfri} Valgfri</option>
                </select>
                <small>{prioritetBeskrivelse[formData.prioritet]}</small>
              </div>

              {/* Årsak */}
              <div className="form-group">
                <label>Årsak *</label>
                <textarea
                  value={formData.arsak}
                  onChange={(e) => handleInputChange('arsak', e.target.value)}
                  rows="3"
                  required
                />
              </div>

              {/* Omfang */}
              <div className="form-group">
                <label>Omfang *</label>
                <textarea
                  value={formData.omfang}
                  onChange={(e) => handleInputChange('omfang', e.target.value)}
                  rows="5"
                  required
                />
              </div>

              {/* Metode */}
              <div className="form-group">
                <label>Metode</label>
                <textarea
                  value={formData.metode}
                  onChange={(e) => handleInputChange('metode', e.target.value)}
                  rows="2"
                />
              </div>

              {/* Egendefinert tillegg */}
              <div className="form-group">
                <label>Egne presiseringer / tillegg</label>
                <textarea
                  value={formData.egendefinert_tillegg}
                  onChange={(e) => handleInputChange('egendefinert_tillegg', e.target.value)}
                  rows="2"
                  placeholder="Tillegg eller spesifikke detaljer for denne eiendommen..."
                />
              </div>

              {/* Kostnadsestimat */}
              <div className="form-row">
                <div className="form-group">
                  <label>Estimert kostnad fra (kr)</label>
                  <input
                    type="number"
                    value={formData.kostnad_min}
                    onChange={(e) => handleInputChange('kostnad_min', e.target.value)}
                    min="0"
                    step="1000"
                  />
                </div>
                <div className="form-group">
                  <label>Estimert kostnad til (kr)</label>
                  <input
                    type="number"
                    value={formData.kostnad_max}
                    onChange={(e) => handleInputChange('kostnad_max', e.target.value)}
                    min="0"
                    step="1000"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Kommentar til kostnad</label>
                <input
                  type="text"
                  value={formData.kostnad_kommentar}
                  onChange={(e) => handleInputChange('kostnad_kommentar', e.target.value)}
                  placeholder="F.eks. 'Avhenger av omfang av skade'"
                />
              </div>

              {/* Hastegrad */}
              <div className="form-group">
                <label>Hastegrad</label>
                <select
                  value={formData.hastegrad}
                  onChange={(e) => handleInputChange('hastegrad', e.target.value)}
                >
                  <option value="akutt">Akutt - Innen 1 måned</option>
                  <option value="haster">Haster - Innen 3 måneder</option>
                  <option value="normal">Normal - Innen 6 måneder</option>
                  <option value="lav">Lav - Kan vente 1+ år</option>
                </select>
              </div>

              {/* Knapper */}
              <div className="form-buttons">
                <button type="submit" disabled={lagrer} className="btn-lagre">
                  {lagrer ? 'Lagrer...' : '✅ Lagre anbefaling'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setVisSkjema(false)
                    setValgtKategori('')
                  }}
                  className="btn-avbryt"
                >
                  Avbryt
                </button>
              </div>
            </>
          )}
        </form>
      )}
    </div>
  )
}

export default VidereUndersokelser
